import Bacon from 'baconjs';
import * as ramda from 'ramda';

import {convertToArray} from 'nutshell-core/filter';

import {sanitizeFiltersForView} from './sanitize-filters-for-view';

export function handleFilterUpdate(selectedFilters, {oldFilter, newFilter}) {
    return selectedFilters.map((selectedFilter) =>
        selectedFilter === oldFilter ? newFilter : selectedFilter
    );
}

export function handleFilterRemove(selectedFilters, removedFilters) {
    return ramda.difference(selectedFilters, removedFilters);
}

function addFilterForAudience(filter, audienceId) {
    if (!filter) {
        return {
            audiences: {
                data: [{data: audienceId}],
            },
        };
    } else {
        filter.audiences = {
            data: [{data: audienceId}],
        };

        return filter;
    }
}

/**
 * Called when the URL changes, or a saved filter is loaded. Ensures the
 * loaded filterset does not include multiple pipelines, and adjusts if
 * necessary. Also adds an audience filter if an audience is specified in the URL.
 *
 * @param  {Object} newQuery       - Full query object from URL
 * @return {Object[]}              - Array of sanitized filters
 */
function sanitizeFiltersForQuery(newQuery) {
    if (!newQuery) return [];

    if (newQuery.audience) {
        newQuery.filter = addFilterForAudience(newQuery.filter, newQuery.audience);
    }

    const filterArray = convertToArray(newQuery.filter);

    return sanitizeFiltersForView(filterArray, newQuery.view);
}

export default function (dispatcher) {
    const {push, stream} = dispatcher;
    const urlStream = stream('URL').map('.query');

    const selected = Bacon.update(
        [],
        [urlStream],
        (unused, query) => sanitizeFiltersForQuery(query),
        [stream('FILTERS-ADD')],
        (selectedFilters, addedFilters) => selectedFilters.concat(addedFilters),
        [stream('FILTERS-REMOVE')],
        handleFilterRemove,
        [stream('QUERY-LOAD-SAVED')],
        (unused, newQuery) => sanitizeFiltersForQuery(newQuery),
        [stream('VIEW-UPDATE')],
        sanitizeFiltersForView,
        [stream('FILTERS-UPDATE')],
        handleFilterUpdate,
        [stream('FILTERS-REMOVE-ALL').merge(stream('QUERY-RESET-TO-DEFAULT'))],
        () => []
    );

    return Bacon.combineTemplate({
        selected,

        addFilters(filters) {
            push('FILTERS-ADD', ramda.flatten([filters]));
        },
        removeFilters(filters) {
            push('FILTERS-REMOVE', ramda.flatten([filters]));
        },
        updateFilter(oldFilter, newFilter) {
            push('FILTERS-UPDATE', {oldFilter, newFilter});
        },
        removeAllFilters() {
            push('FILTERS-REMOVE-ALL');
        },
    });
}
